// Pantalla admin: tabla de usuarios filtrable con asignación de profe + licencia.

import { useEffect, useState } from 'react';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { AssignmentDialog } from '@/components/organisms/AssignmentDialog';
import { ApiError } from '@/services/api';
import { listAdminUsers, type AdminUser, type ListUsersFilter } from '@/services/admin';
import { listLicenses, type CatalogLicense } from '@/services/catalogs';
import styles from './AdminUsuarios.module.scss';

export function AdminUsuarios() {
  const [items, setItems] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [licenses, setLicenses] = useState<CatalogLicense[]>([]);

  // Filtros
  const [role, setRole] = useState<string>('student');
  const [q, setQ] = useState('');
  const [licenseId, setLicenseId] = useState<string>('');
  const [noTeacher, setNoTeacher] = useState(false);

  // Dialog
  const [editing, setEditing] = useState<AdminUser | null>(null);

  useEffect(() => {
    listLicenses().then(setLicenses).catch(() => {});
  }, []);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const filter: ListUsersFilter = { role, limit: 100 };
      if (q.trim()) filter.q = q.trim();
      if (licenseId) filter.license_id = Number(licenseId);
      if (noTeacher) filter.no_teacher = true;
      const r = await listAdminUsers(filter);
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [role, licenseId, noTeacher]);

  return (
    <>
      <Header title="Usuarios" />
      <main className={styles.main}>
        <section className={styles.filters}>
          <label className={styles.field}>
            <span>Rol</span>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="student">Alumnos</option>
              <option value="teacher">Profesores</option>
              <option value="admin">Admins</option>
              <option value="company_admin">Admin empresa</option>
              <option value="inspector">Inspectores</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>Buscar</span>
            <form onSubmit={(e) => { e.preventDefault(); void reload(); }}>
              <input
                type="search"
                placeholder="email o nombre"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </form>
          </label>

          {role === 'student' && (
            <>
              <label className={styles.field}>
                <span>Licencia</span>
                <select value={licenseId} onChange={(e) => setLicenseId(e.target.value)}>
                  <option value="">Todas</option>
                  {licenses.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
              </label>
              <label className={`${styles.field} ${styles.checkbox}`}>
                <input type="checkbox" checked={noTeacher} onChange={(e) => setNoTeacher(e.target.checked)} />
                Sin profesor asignado
              </label>
            </>
          )}
        </section>

        <p className={styles.muted}>{total} {total === 1 ? 'usuario' : 'usuarios'} encontrados</p>

        {error && <p className={styles.error}>{error}</p>}

        {loading ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Licencia</th>
                  <th>Profesor asignado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id}>
                    <td><strong>{u.display_name}</strong></td>
                    <td className={styles.muted}>{u.email}</td>
                    <td>{u.roles.split(',').filter(Boolean).map((r) => <Badge key={r} tone="neutral">{r}</Badge>)}</td>
                    <td>{u.license_slug ? <Badge tone="accent">{u.license_slug}</Badge> : <span className={styles.muted}>—</span>}</td>
                    <td>{u.assigned_teacher_name?.trim() || <span className={styles.muted}>sin asignar</span>}</td>
                    <td>
                      {u.roles.split(',').includes('student') && (
                        <Button variant="secondary" size="sm" onClick={() => setEditing(u)}>Editar</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={6} className={styles.empty}>Sin resultados</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <AssignmentDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        user={editing}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </>
  );
}
